// In-memory cache layer for stylesheet snapshots and write modes.
// Example: mode "patch" indicates insertRule/deleteRule diff updates.

const previousCssByStyleTag = new WeakMap<HTMLStyleElement, string>()
const previousWriteModeByStyleTag = new WeakMap<HTMLStyleElement, 'patch' | 'replaceSync' | 'textContent' | 'unchanged'>()
const previousSplitRulesByStyleTag = new WeakMap<HTMLStyleElement, string[]>()

// Read previous CSS snapshot from cache or current style tag content.
export function resolvePreviousCss(styleTag: HTMLStyleElement): string {
  const cached = previousCssByStyleTag.get(styleTag)
  // Prefer cached snapshot for stable diff comparisons.
  if (typeof cached === 'string') {
    return cached
  } else {
    const text = styleTag.textContent ?? ''
    previousCssByStyleTag.set(styleTag, text)
    // Return initialized cache snapshot from style tag content.
    return text
  }
}

// Save css snapshot and write mode for one style tag.
export function setCachedCssSnapshot(
  styleTag: HTMLStyleElement,
  css: string,
  mode: 'patch' | 'replaceSync' | 'textContent' | 'unchanged',
  splitRules?: string[],
): void {
  previousCssByStyleTag.set(styleTag, css)
  previousWriteModeByStyleTag.set(styleTag, mode)
  if (splitRules) {
    previousSplitRulesByStyleTag.set(styleTag, splitRules)
  }
}

// Read cached split rules for one style tag (avoids re-splitting previousCss).
export function readCachedSplitRules(styleTag: HTMLStyleElement): string[] | undefined {
  return previousSplitRulesByStyleTag.get(styleTag)
}

// Read last cached CSS snapshot for one style tag.
export function readCachedCssFromStyleTag(styleTag: HTMLStyleElement): string {
  // Return cached css or current style text fallback.
  return resolvePreviousCss(styleTag)
}

// Read last write mode used for one style tag update.
export function readCssWriteModeFromStyleTag(
  styleTag: HTMLStyleElement,
): 'patch' | 'replaceSync' | 'textContent' | 'unchanged' {
  // Return default mode when style tag has not been written yet.
  if (!previousWriteModeByStyleTag.has(styleTag)) {
    return 'unchanged'
  } else {
    // Return cached write mode for style tag.
    return previousWriteModeByStyleTag.get(styleTag) ?? 'unchanged'
  }
}

// Read current `CSSStyleSheet.cssRules` text list from one style tag.
export function readSheetRulesFromStyleTag(styleTag: HTMLStyleElement): string[] {
  const sheet = styleTag.sheet
  // Return empty list when style tag has no bound sheet yet.
  if (!sheet) {
    return []
  } else {
    // Continue collecting cssRules text output.
  }

  const rules: string[] = []
  // Walk stylesheet rules in order to keep rendered cascade order visible.
  for (let index = 0; index < sheet.cssRules.length; index += 1) {
    rules.push(sheet.cssRules[index].cssText)
  }

  // Return stylesheet rules text snapshot.
  return rules
}
